import type { DevelopmentProfile, DevelopmentReadinessStatus, DevelopmentScenario } from "../types";
import { mortgageConstant, loanBalance } from "../engine/engine";
import { buildDevelopmentBudget } from "./developmentBudget";
import { calcDevelopmentScenario } from "./development";
import { money0, pct, x2 } from "./format";

export interface ReadinessDefinition {
  id: string;
  phase: 1 | 2 | 3;
  label: string;
  weight: number;
  critical: boolean;
  defaultStatus: DevelopmentReadinessStatus;
  evidence: string;
  nextSource: string;
}

export const TAYLOR_PARK_READINESS: ReadinessDefinition[] = [
  { id: "parcel", phase: 1, label: "Parcel geometry and ownership", weight: 6, critical: false, defaultStatus: "verified", evidence: "City GIS folio 07-2216-000-0220 and owner record", nextSource: "Current title commitment and ALTA survey" },
  { id: "aerial", phase: 1, label: "Current aerial and site constraints", weight: 4, critical: false, defaultStatus: "verified", evidence: "Miami-Dade 2025 orthophotography with City parcel overlay", nextSource: "Boundary/topographic survey and wetland/water-body delineation" },
  { id: "survey-title", phase: 1, label: "Survey, title and net developable acreage", weight: 8, critical: true, defaultStatus: "missing", evidence: "The GIS parcel appears to include the quarry lake; net upland acreage is unresolved", nextSource: "Florida-licensed surveyor, title company and civil engineer" },
  { id: "zoning-letter", phase: 1, label: "Formal zoning and future-land-use letter", weight: 8, critical: true, defaultStatus: "working", evidence: "City GIS shows CF zoning and Recreation/Open Space future land use", nextSource: "North Miami Beach Comprehensive Plan and Zoning Verification Letter" },
  { id: "concept-plan", phase: 2, label: "Test-fit concept and phasing plan", weight: 8, critical: true, defaultStatus: "missing", evidence: "Unit counts are planning concepts, not a parcel-tested site plan", nextSource: "Architect, civil engineer, traffic engineer and landscape architect" },
  { id: "environment-status", phase: 2, label: "Current environmental status", weight: 8, critical: true, defaultStatus: "working", evidence: "Brownfield/BSRA status is confirmed; no SRCO date is recorded", nextSource: "Complete ERIC_9217 and DERM files plus agency status conference" },
  { id: "environment-cost", phase: 2, label: "Environmental cost-to-closure", weight: 8, critical: true, defaultStatus: "missing", evidence: "No quantity-backed remediation, cap, vapor or monitoring estimate", nextSource: "Environmental engineer working from the full agency file" },
  { id: "utilities", phase: 2, label: "Water, sewer and off-site capacity", weight: 8, critical: true, defaultStatus: "missing", evidence: "No current will-serve or pump-station capacity letter", nextSource: "NMB Utilities and Miami-Dade WASD" },
  { id: "geotech-drainage", phase: 2, label: "Geotechnical, lake-edge and drainage basis", weight: 6, critical: false, defaultStatus: "missing", evidence: "No boring program, slope review, groundwater model or drainage concept", nextSource: "Geotechnical, civil and environmental engineers" },
  { id: "market", phase: 2, label: "Apartment, retail and absorption study", weight: 8, critical: true, defaultStatus: "missing", evidence: "Rents and absorption are working assumptions", nextSource: "Independent market analyst and multifamily/retail brokers" },
  { id: "operating", phase: 2, label: "Operating expense and insurance budget", weight: 6, critical: false, defaultStatus: "missing", evidence: "Expense ratio is a concept percentage, not a line-item operating budget", nextSource: "Property manager, insurance broker and tax counsel" },
  { id: "cost-estimate", phase: 2, label: "Quantity-backed development cost plan", weight: 8, critical: true, defaultStatus: "missing", evidence: "Current model allocates a Level-5 range", nextSource: "Preconstruction GC or independent cost estimator" },
  { id: "ground-lease", phase: 3, label: "Financeable ground lease and tax treatment", weight: 8, critical: true, defaultStatus: "missing", evidence: "The $1/year, 99-year structure is a proposal, not a lender-ready term sheet", nextSource: "City, real-estate counsel, tax counsel and construction lender" },
  { id: "capital", phase: 3, label: "Committed capital and incentive plan", weight: 6, critical: false, defaultStatus: "working", evidence: "Programs are identified but no project-specific source is committed", nextSource: "City/CRA, FDEP, lenders, equity partners and tax counsel" },
];

const READINESS_FACTOR: Record<DevelopmentReadinessStatus, number> = { missing: 0, working: 0.5, verified: 1 };

export function developmentReadiness(profile: DevelopmentProfile) {
  const items = TAYLOR_PARK_READINESS.map((item) => ({
    ...item,
    status: profile.readiness?.[item.id] ?? item.defaultStatus,
  }));
  const score = Math.round(items.reduce((sum, item) => sum + item.weight * READINESS_FACTOR[item.status], 0));
  const criticalMissing = items.filter((item) => item.critical && item.status === "missing");
  const level = score >= 80 && criticalMissing.length === 0 ? "green" : score >= 50 ? "amber" : "red";
  return { score, level, items, criticalMissing, verified: items.filter((item) => item.status === "verified").length } as const;
}

export interface DevelopmentCashFlowRow {
  year: number;
  phase: "construction" | "lease-up" | "stabilized";
  grossRevenue: number;
  effectiveGrossIncome: number;
  operatingExpenses: number;
  noi: number;
  debtService: number;
  cashFlowAfterDebt: number;
  dscr: number;
  value: number;
  loanBalance: number;
}

export function projectDevelopmentCashFlow(profile: DevelopmentProfile, scenario: DevelopmentScenario, years = 10) {
  const assumptions = profile.budget;
  const budget = buildDevelopmentBudget(profile, scenario, assumptions);
  const stabilized = calcDevelopmentScenario(scenario);
  const constructionYears = Math.max(1, Math.ceil(assumptions.constructionMonths / 12));
  const permanentLoan = Math.min(budget.total * assumptions.permanentLtv, stabilized.stabilizedValue * assumptions.permanentLtv);
  const annualDebtService = permanentLoan * mortgageConstant(assumptions.permanentRate, assumptions.permanentAmortYears);
  const rows: DevelopmentCashFlowRow[] = [];
  let cumulativeCashFlow = 0;

  for (let year = 1; year <= years; year += 1) {
    const operatingYear = year - constructionYears;
    const ramp = operatingYear <= 0 ? 0 : operatingYear === 1 ? 0.55 : operatingYear === 2 ? 0.85 : 1;
    const phase = ramp === 0 ? "construction" : ramp < 1 ? "lease-up" : "stabilized";
    const growthYear = Math.max(0, operatingYear - 1);
    const grossRevenue = stabilized.grossPotentialRevenue * Math.pow(1 + assumptions.rentGrowthPct, growthYear) * ramp;
    const effectiveGrossIncome = grossRevenue * (1 - scenario.vacancyPct);
    const operatingExpenses = stabilized.operatingExpenses * Math.pow(1 + assumptions.expenseGrowthPct, growthYear) * ramp;
    const noi = Math.max(0, effectiveGrossIncome - operatingExpenses);
    const debtService = operatingYear > 0 ? annualDebtService : 0;
    const cashFlowAfterDebt = noi - debtService;
    cumulativeCashFlow += cashFlowAfterDebt;
    const debtYears = Math.max(0, operatingYear);
    const balance = debtYears ? loanBalance(permanentLoan, assumptions.permanentRate, assumptions.permanentAmortYears, debtYears) : permanentLoan;
    rows.push({
      year,
      phase,
      grossRevenue,
      effectiveGrossIncome,
      operatingExpenses,
      noi,
      debtService,
      cashFlowAfterDebt,
      dscr: debtService > 0 ? noi / debtService : 0,
      value: noi > 0 ? noi / scenario.exitCap : 0,
      loanBalance: balance,
    });
  }

  const end = rows[rows.length - 1];
  const nextNoi = end.noi * (1 + assumptions.rentGrowthPct);
  const terminalValue = scenario.exitCap > 0 ? nextNoi / scenario.exitCap : 0;
  const netSaleProceeds = terminalValue * (1 - assumptions.saleCostsPct) - end.loanBalance;
  const equityMultiple = budget.sponsorEquity > 0 ? (cumulativeCashFlow + netSaleProceeds) / budget.sponsorEquity : 0;
  return { rows, budget, stabilized, constructionYears, permanentLoan, annualDebtService, terminalValue, netSaleProceeds, cumulativeCashFlow, equityMultiple };
}

export function developmentSensitivity(scenario: DevelopmentScenario) {
  const rentCases = [-0.1, 0, 0.1];
  const costCases = [-0.1, 0, 0.1];
  return rentCases.map((rentChange) => ({
    rentChange,
    cells: costCases.map((costChange) => {
      const adjusted = {
        ...scenario,
        oneBrRent: scenario.oneBrRent * (1 + rentChange),
        twoBrRent: scenario.twoBrRent * (1 + rentChange),
        retailRentPsf: scenario.retailRentPsf * (1 + rentChange),
        costLow: scenario.costLow * (1 + costChange),
        costHigh: scenario.costHigh * (1 + costChange),
      };
      return { costChange, gap: calcDevelopmentScenario(adjusted).valueCostGap };
    }),
  }));
}

export function buildDevelopmentGapReport(profile: DevelopmentProfile, scenario: DevelopmentScenario) {
  const readiness = developmentReadiness(profile);
  const projection = projectDevelopmentCashFlow(profile, scenario);
  const current = projection.stabilized;
  const missing = readiness.criticalMissing.slice(0, 4).map((item) => item.label).join(", ");
  const verdict = readiness.level === "green" ? "READY FOR INVESTMENT-COMMITTEE ANALYSIS" : "HOLD FOR DILIGENCE";
  return [
    `${verdict} - Taylor Park is a documented redevelopment opportunity, but the current CF zoning and Recreation/Open Space future-land-use designation do not permit the modeled private multifamily/retail program by right.`,
    `The ${scenario.name} concept produces ${money0(current.noi)} of stabilized NOI and ${money0(current.stabilizedValue)} of indicated value at a ${pct(scenario.exitCap)} exit cap, compared with ${money0(current.costMid)} of midpoint concept cost. The resulting value-to-cost gap is ${money0(current.valueCostGap)} and the modeled value/cost ratio is ${pct(current.valueToCost)}.`,
    `The ten-year operating projection assumes ${profile.budget.constructionMonths} months of construction, two years of lease-up, ${pct(profile.budget.rentGrowthPct)} annual rent growth, ${pct(profile.budget.expenseGrowthPct)} expense growth and ${x2(projection.equityMultiple)} modeled equity multiple before tax. These are scenario outputs, not forecasts.`,
    `Readiness is ${readiness.score}/100. ${readiness.criticalMissing.length ? `Critical missing evidence: ${missing}.` : "No critical evidence item is marked missing."}`,
    `Next decision: establish net developable upland acreage, obtain the formal zoning/FLUM letter, price environmental closure and utilities, and test a survey-based concept before committing entitlement or capital-raise expense.`,
  ].join("\n\n");
}
